import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { getRoles } from '../api/auth';
import AddGroupModal from './addGroupModal';
import DeleteGroupModal from './deleteGroupModal';
import GroupPermissionsModal from './groupPermissionsModal';
import { getApiErrorMessage } from '../utils/apiError';

// DeleteRole bu grup için 403 döner.
const PROTECTED_GROUPS = ['admin'];

const isProtectedGroup = name =>
  PROTECTED_GROUPS.includes((name || '').toLowerCase());

const GroupsTab = () => {
  const {
    data: groups,
    error,
    isLoading,
    refetch,
  } = useQuery('roles', getRoles, {
    refetchOnWindowFocus: false,
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState(null);

  const handleDeleteModalOpen = groupName => {
    setSelectedGroupName(groupName);
    setIsDeleteModalOpen(true);
  };

  const handlePermissionsModalOpen = groupName => {
    setSelectedGroupName(groupName);
    setIsPermissionsModalOpen(true);
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={5}
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {getApiErrorMessage(error)}</Text>
      </Box>
    );
  }

  return (
    <Box flex="1" minHeight={0} display="flex" flexDirection="column" w="100%">
      <TableContainer flex="1" minHeight={0} overflowY="auto" w="100%">
        <Table variant="simple" colorScheme="gray" size="sm" w="100%">
          <Thead position="sticky" top={0} zIndex={1} bg="white">
            <Tr>
              <Th px={4} py={2}>
                Group Name
              </Th>
              <Th px={4} py={2}>
                Description
              </Th>
              <Th px={4} py={2}>
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {Array.isArray(groups) && groups.length > 0 ? (
              groups.map(group => {
                const isProtected = isProtectedGroup(group.name);
                return (
                  <Tr key={group.name} height="40px">
                    <Td px={4} py={2}>
                      <Flex align="center" gap={2}>
                        {group.name}
                        {isProtected && (
                          <Badge colorScheme="gray" textTransform="none">
                            protected
                          </Badge>
                        )}
                      </Flex>
                    </Td>
                    <Td px={4} py={2}>
                      {group.description || '-'}
                    </Td>
                    <Td px={4} py={2}>
                      {isProtected ? (
                        <Text as="span">-</Text>
                      ) : (
                        <Flex align="center">
                          <Text
                            as="span"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() =>
                              handlePermissionsModalOpen(group.name)
                            }
                          >
                            Manage Permissions
                          </Text>
                          <Text as="span" mx={2}>
                            |
                          </Text>
                          <Text
                            as="span"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => handleDeleteModalOpen(group.name)}
                          >
                            Delete
                          </Text>
                        </Flex>
                      )}
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={3}>
                  <Text textAlign="center">No groups found.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Box mt={3} mb={4}>
        <Flex justifyContent="flex-end">
          <Button
            size="sm"
            colorScheme="gray"
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Group
          </Button>
        </Flex>
      </Box>

      <AddGroupModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        refetch={refetch}
      />
      <DeleteGroupModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        groupName={selectedGroupName}
        refetch={refetch}
      />
      {isPermissionsModalOpen && (
        <GroupPermissionsModal
          isOpen={isPermissionsModalOpen}
          onClose={() => setIsPermissionsModalOpen(false)}
          groupName={selectedGroupName}
        />
      )}
    </Box>
  );
};

export default GroupsTab;
